import type { MemedPrefillItem } from '../services/memedService';

export type MemedWorkspaceLaunchParams = {
  patientId: string;
  professionalId?: string | null;
  appointmentId?: string | null;
  medicalRecordId?: string | null;
  items?: MemedPrefillItem[];
};

const PREFILL_STORAGE_PREFIX = 'klinvia-memed-prefill:';

export function buildMemedWorkspaceSearchParams(params: MemedWorkspaceLaunchParams): URLSearchParams {
  const q = new URLSearchParams();
  q.set('patient_id', params.patientId);
  if (params.professionalId) q.set('professional_id', params.professionalId);
  if (params.appointmentId) q.set('appointment_id', params.appointmentId);
  if (params.medicalRecordId) q.set('medical_record_id', params.medicalRecordId);

  if (params.items && params.items.length > 0) {
    const key = `${PREFILL_STORAGE_PREFIX}${crypto.randomUUID()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(params.items));
      q.set('prefill_key', key);
    } catch {
      // Itens grandes: abre sem pré-carga — o prescritor adiciona manualmente.
    }
  }
  return q;
}

export function buildMemedWorkspacePath(params: MemedWorkspaceLaunchParams): string {
  return `/clinic/memed-prescribe?${buildMemedWorkspaceSearchParams(params).toString()}`;
}

/**
 * Abre a Memed em nova aba. A aba atual do Klinvia segue utilizável (agenda, inbox, etc.).
 */
export function openMemedPrescriptionWorkspace(params: MemedWorkspaceLaunchParams): boolean {
  const path = buildMemedWorkspacePath(params);
  const tab = window.open(path, '_blank', 'noopener,noreferrer');
  return Boolean(tab);
}

export function readMemedWorkspacePrefill(prefillKey: string | null): MemedPrefillItem[] {
  if (!prefillKey || !prefillKey.startsWith(PREFILL_STORAGE_PREFIX)) return [];
  try {
    const raw = sessionStorage.getItem(prefillKey);
    sessionStorage.removeItem(prefillKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function parseMemedWorkspaceQuery(searchParams: URLSearchParams): MemedWorkspaceLaunchParams | null {
  const patientId = searchParams.get('patient_id')?.trim();
  if (!patientId) return null;
  const prefillKey = searchParams.get('prefill_key');
  const items = readMemedWorkspacePrefill(prefillKey);
  return {
    patientId,
    professionalId: searchParams.get('professional_id'),
    appointmentId: searchParams.get('appointment_id'),
    medicalRecordId: searchParams.get('medical_record_id'),
    items: items.length > 0 ? items : undefined,
  };
}
